import { FacturationGroup } from './facturation'

export type AgencyRef = { id: string; name: string }

export const EMAIL_FACTURATION_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function hasInvalidFacturationEmails(groups: FacturationGroup[]): boolean {
  return groups.some(
    g => g.emailFacturation && !EMAIL_FACTURATION_REGEX.test(g.emailFacturation)
  )
}

/**
 * Déplace une agence entre groupes (même règles que la page Facturation) :
 * - retrait d'un groupe non-défaut → l'agence revient au groupe défaut
 * - ajout depuis le défaut → retirée du défaut (impossible si c'est la seule agence du défaut)
 * - ajout depuis un autre groupe → retirée de tous les autres
 */
export function applyAgencySelectionChange(
  groups: FacturationGroup[],
  agenciesPool: AgencyRef[],
  targetGroupId: string,
  newAgencyIds: string[]
): { groups: FacturationGroup[]; error?: string } {
  const targetGroup = groups.find(g => g.facturationGroupId === targetGroupId)
  if (!targetGroup || targetGroup.default) {
    return { groups }
  }

  const oldIds = targetGroup.agencies.map(a => a.id)

  if (newAgencyIds.length < oldIds.length) {
    const removedId = oldIds.find(id => !newAgencyIds.includes(id))
    const removedAgency = agenciesPool.find(a => a.id === removedId)
    return {
      groups: groups.map(g => {
        if (g.default) {
          return {
            ...g,
            agencies: removedAgency
              ? g.agencies.concat(
                  g.agencies.some(a => a.id === removedAgency.id) ? [] : [removedAgency]
                )
              : g.agencies,
          }
        }
        if (g.facturationGroupId === targetGroupId) {
          return {
            ...g,
            agencies: g.agencies.filter(a => a.id !== removedAgency?.id),
          }
        }
        return g
      }),
    }
  }

  const addedId = newAgencyIds.find(id => !oldIds.includes(id))
  if (!addedId) {
    return { groups }
  }

  const defaultGroup = groups.find(g => g.default)
  if (
    defaultGroup?.agencies.length === 1 &&
    defaultGroup.agencies.map(a => a.id).includes(addedId)
  ) {
    return {
      groups,
      error:
        "Impossible d'ajouter cette agence à un autre groupe car elle est la seule agence du groupe par défaut.",
    }
  }

  const addedAgency = agenciesPool.find(a => a.id === addedId)
  if (!addedAgency) {
    return { groups }
  }

  return {
    groups: groups.map(g => {
      if (g.default || g.facturationGroupId !== targetGroupId) {
        return {
          ...g,
          agencies: g.agencies.filter(a => a.id !== addedAgency.id),
        }
      }
      return {
        ...g,
        agencies: g.agencies.concat(
          g.agencies.some(a => a.id === addedAgency.id) ? [] : [addedAgency]
        ),
      }
    }),
  }
}
